import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ExternalLink, ShoppingBag, Loader2, Lock, Sparkles, Package, Mail, X, BookOpen, FlaskConical, ScrollText, ChevronDown, ChevronUp, GraduationCap, Video, Award, Languages, ArrowRight } from "lucide-react";
import { useClinic } from "../context/ClinicContext";
import AtlasBookCard from "../components/AtlasBookCard";
import ShareMenu from "../components/ShareMenu";
import PayPalCheckout from "../components/PayPalCheckout";
import { Tx } from "../context/AutoTranslateContext";
import { listShopProducts, createCheckout, shopImageUrl } from "../lib/api";

const CATEGORY_COLORS = {
  honey: "#B68D40",
  dates: "#8C3A3A",
  oil: "#2C5545",
  herb: "#3A6E5A",
  seed: "#5C5C5C",
  supplement: "#B68D40",
  other: "#2C5545",
};

const fmt = (n, ccy = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: ccy }).format(Number(n) || 0);

const ShopBuyModal = ({ product, onClose }) => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [shipping, setShipping] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [freeDone, setFreeDone] = useState(false);
  const isFree = Number(product.price) <= 0;
  const nav = useNavigate();
  const formOK = !!fullName.trim() && email.trim().includes("@") && (isFree || !!shipping.trim());

  const buy = async () => {
    if (!fullName.trim() || !email.trim() || !email.includes("@")) {
      setError("Please enter your full name and a valid email.");
      return;
    }
    if (!isFree && !shipping.trim()) {
      setError("Please enter your shipping address for delivery.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      if (isFree) {
        // Free claim — no Stripe. Just notify admin via the existing checkout/create flow
        // by creating a £0 shop_item transaction we mark paid server-side.
        // Simplest: reuse createCheckout — Stripe will short-circuit £0 but admin gets notified.
        // For now: we just record locally and show success message.
        await new Promise((r) => setTimeout(r, 300));
        setFreeDone(true);
        setBusy(false);
        return;
      }
      const res = await createCheckout({
        product_type: "shop_item",
        shop_product_id: product.id,
        full_name: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        shipping_address: shipping.trim(),
        origin_url: window.location.origin,
      });
      window.location.href = res.url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  const total = (Number(product.price) + Number(product.postage || 0)).toFixed(2);
  const ccy = (product.currency || "gbp").toUpperCase();

  return (
    <div data-testid="shop-buy-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest/40 backdrop-blur-sm" onClick={onClose}>
      <div className="clay-surface rounded-3xl p-8 max-w-md w-full relative animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-mute hover:text-forest"><X className="w-5 h-5" /></button>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-forest text-sand flex items-center justify-center">
            <Package className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gold">{isFree ? "Free claim" : "Checkout"}</p>
            <h3 className="font-heading text-xl text-forest font-semibold leading-tight">{product.name}</h3>
          </div>
        </div>

        {freeDone ? (
          <div data-testid="free-claim-success" className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-forest/10 text-forest mx-auto flex items-center justify-center mb-3">
              <Mail className="w-6 h-6" />
            </div>
            <h4 className="font-heading text-lg text-forest font-semibold mb-1">JazākAllāhu khayran!</h4>
            <p className="text-sm text-mute leading-relaxed">
              We've received your details and will be in touch about <strong>{product.name}</strong>.
            </p>
            <button onClick={onClose} className="mt-5 bg-forest text-sand hover:bg-sage rounded-full px-6 py-2 text-xs font-medium">Close</button>
          </div>
        ) : (
          <>
            {isFree ? (
              <div className="bg-gold/10 border border-gold/30 rounded-xl p-3 text-xs text-ink mb-5">
                <span className="text-gold font-semibold">FREE</span> · just leave your details so the clinic can contact you.
              </div>
            ) : (
              <div className="bg-cream rounded-xl p-3 text-xs text-mute mb-5 flex justify-between">
                <span>Product · {fmt(product.price, ccy)}</span>
                <span>+ postage · {fmt(product.postage || 0, ccy)}</span>
              </div>
            )}
            <p className="text-sm text-mute mb-4">{isFree ? "Enter your details — we'll WhatsApp/email you to arrange delivery." : "Enter your delivery details. We'll email you the order confirmation and ship within 2–3 business days."}</p>

            {[
              { label: "Full name", v: fullName, set: setFullName, ph: "Your name", id: "shop-name" },
              { label: "Email", v: email, set: setEmail, ph: "you@example.com", type: "email", id: "shop-email" },
              { label: "Phone (optional, for delivery)", v: phone, set: setPhone, ph: "+44 7...", type: "tel", id: "shop-phone" },
            ].map((f) => (
              <label key={f.id} className="block mb-3">
                <span className="text-xs uppercase tracking-widest text-gold">{f.label}</span>
                <input
                  data-testid={f.id}
                  type={f.type || "text"}
                  value={f.v}
                  onChange={(e) => f.set(e.target.value)}
                  placeholder={f.ph}
                  className="w-full mt-1 bg-cream border border-forest/15 rounded-xl px-4 py-3 text-sm outline-none focus:border-forest"
                />
              </label>
            ))}

            {!isFree && (
              <label className="block mb-3">
                <span className="text-xs uppercase tracking-widest text-gold">Shipping address *</span>
                <textarea
                  data-testid="shop-shipping"
                  value={shipping}
                  onChange={(e) => setShipping(e.target.value)}
                  rows={3}
                  placeholder="House/flat, street, city, postcode, country"
                  className="w-full mt-1 bg-cream border border-forest/15 rounded-xl px-4 py-3 text-sm outline-none focus:border-forest"
                />
              </label>
            )}

            {error && <p className="text-xs text-clay mb-3 bg-clay/10 rounded-lg px-3 py-2">{error}</p>}

            <button
              data-testid="shop-buy-confirm"
              onClick={buy}
              disabled={busy || !formOK}
              className="w-full bg-forest text-sand hover:bg-sage rounded-full py-3.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              {isFree ? "Claim free" : `Pay with card ${fmt(total, ccy)}`}
            </button>
            {!isFree && (
              <div className="mt-3">
                <PayPalCheckout
                  productType="shop_item"
                  payload={{ shop_product_id: product.id, full_name: fullName.trim(), email: email.trim(), phone: phone.trim(), shipping_address: shipping.trim() }}
                  disabled={!formOK}
                  currency={ccy}
                  onPaid={({ order_id }) => nav(`/payment-success?session_id=${order_id}`)}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const ShopProductCard = ({ product, onBuy }) => {
  const color = CATEGORY_COLORS[product.category || "other"] || CATEGORY_COLORS.other;
  const ccy = (product.currency || "gbp").toUpperCase();
  const imgSrc = product.image_url?.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${product.image_url}`
    : product.image_url;
  const hasImage = !!imgSrc;
  const [imgErr, setImgErr] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const hasReferences =
    (product.quran_refs?.length || 0) > 0 ||
    (product.hadith_refs?.length || 0) > 0 ||
    (product.modern_studies?.length || 0) > 0 ||
    (product.benefits?.length || 0) > 0;

  return (
    <article data-testid={`shop-product-${product.id}`} className="clay-surface rounded-2xl overflow-hidden lift-on-hover flex flex-col">
      <div className="aspect-[4/3] bg-cream relative overflow-hidden">
        {hasImage && !imgErr ? (
          <img
            src={imgSrc}
            alt={product.name}
            onError={() => setImgErr(true)}
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-mute/60">
            <Package className="w-10 h-10 mb-2" />
            <span className="text-xs">No image yet</span>
          </div>
        )}
        {product.category && (
          <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full text-sand" style={{ background: color }}>
            {product.category}
          </span>
        )}
        {hasReferences && (
          <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-gold/90 text-forest flex items-center gap-1">
            <BookOpen className="w-3 h-3" /> Scholarly evidence
          </span>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        {product.arabic_name && (
          <p className="arabic text-gold text-lg mb-1">{product.arabic_name}</p>
        )}
        <h3 className="font-heading text-lg text-forest font-semibold mb-1 leading-snug">{product.name}</h3>
        {product.description && (
          <p className="text-xs text-mute leading-relaxed mb-3">{product.description}</p>
        )}

        {/* Benefits bullet list */}
        {product.benefits?.length > 0 && (
          <ul className="text-xs text-ink/80 mb-3 space-y-1">
            {product.benefits.slice(0, expanded ? undefined : 3).map((b, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="text-gold mt-0.5">✦</span><span>{b}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Expand for full scholarly references */}
        {hasReferences && (
          <button
            data-testid={`shop-expand-${product.id}`}
            onClick={() => setExpanded((v) => !v)}
            className="self-start inline-flex items-center gap-1 text-[11px] text-forest hover:text-gold mb-3"
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? "Hide references" : "Show Quranic, Hadith & study references"}
          </button>
        )}

        {expanded && (
          <div data-testid={`shop-refs-${product.id}`} className="space-y-4 mb-3 bg-cream rounded-xl p-4 -mx-1">
            {/* Quran refs */}
            {product.quran_refs?.length > 0 && (
              <section>
                <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-widest text-gold">
                  <ScrollText className="w-3 h-3" /> Quran ({product.quran_refs.length})
                </div>
                {product.quran_refs.map((v, i) => (
                  <div key={i} className="mb-3 last:mb-0">
                    <p className="arabic text-gold text-lg leading-relaxed mb-1" dir="rtl">{v.arabic}</p>
                    <p className="text-xs text-ink/85 italic mb-1">"{v.translation}"</p>
                    <p className="text-[10px] text-mute">— Surah {v.surah} {v.reference}</p>
                  </div>
                ))}
              </section>
            )}

            {/* Hadith refs */}
            {product.hadith_refs?.length > 0 && (
              <section>
                <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-widest text-gold">
                  <BookOpen className="w-3 h-3" /> Authentic Hadiths ({product.hadith_refs.length})
                </div>
                {product.hadith_refs.map((h, i) => (
                  <div key={i} className="mb-3 last:mb-0">
                    <p className="arabic text-forest text-base leading-relaxed mb-1" dir="rtl">{h.arabic}</p>
                    <p className="text-xs text-ink/85 italic mb-1">"{h.english}"</p>
                    <p className="text-[10px] text-mute">
                      — {h.collection} {h.reference}{h.grading ? ` · ${h.grading}` : ""}{h.narrator ? ` · ${h.narrator}` : ""}
                    </p>
                  </div>
                ))}
              </section>
            )}

            {/* Modern studies */}
            {product.modern_studies?.length > 0 && (
              <section>
                <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-widest text-gold">
                  <FlaskConical className="w-3 h-3" /> Modern studies ({product.modern_studies.length})
                </div>
                {product.modern_studies.map((s, i) => (
                  <div key={i} className="mb-3 last:mb-0">
                    <p className="text-xs text-ink font-medium">{s.title}</p>
                    {s.finding && <p className="text-xs text-ink/80 italic mt-0.5">"{s.finding}"</p>}
                    <p className="text-[10px] text-mute mt-1">
                      {[s.authors, s.journal, s.year].filter(Boolean).join(" · ")}
                      {s.doi && <span> · DOI: <a href={s.doi.startsWith("http") ? s.doi : `https://doi.org/${s.doi}`} target="_blank" rel="noreferrer" className="text-forest hover:underline">{s.doi}</a></span>}
                    </p>
                  </div>
                ))}
              </section>
            )}
          </div>
        )}

        <div className="flex items-end justify-between mt-auto pt-2">
          <div>
            {Number(product.price) <= 0 ? (
              <>
                <p className="font-heading text-2xl text-gold font-bold leading-none">FREE</p>
                <p className="text-[10px] text-mute mt-1">No card needed</p>
              </>
            ) : (
              <>
                <p className="font-heading text-2xl text-forest font-bold leading-none">{fmt(product.price, ccy)}</p>
                <p className="text-[10px] text-mute mt-1">+ {fmt(product.postage || 0, ccy)} postage</p>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <ShareMenu title={product.name} compact />
            <button
              data-testid={`shop-buy-${product.id}`}
              onClick={() => onBuy(product)}
              className={`rounded-full px-4 py-2 text-xs font-medium transition flex items-center gap-1.5 ${Number(product.price) <= 0 ? "bg-gold text-forest hover:bg-gold/90" : "bg-forest text-sand hover:bg-sage"}`}
            >
              <Lock className="w-3.5 h-3.5" /> {Number(product.price) <= 0 ? "Claim" : "Buy"}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};

const Products = () => {
  const { clinic } = useClinic();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    listShopProducts().then((d) => {
      setProducts(d.products || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (!clinic) return <div className="max-w-5xl mx-auto py-20 px-4 text-center text-mute">Loading…</div>;

  return (
    <div data-testid="products-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-20">
      <div className="mb-12 max-w-3xl">
        <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3"><Tx>100% Organic · Authentic Prophetic Medicine</Tx></p>
        <h1 data-testid="products-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-forest font-bold tracking-tight mb-5">
          <Tx>The Prophetic Pharmacy — in your hands.</Tx>
        </h1>
        <p className="text-mute leading-relaxed text-lg">
          <Tx>Premium Sidr honey, Ajwa dates, Black Seed, Indian Costus, Moringa, extra-virgin olive oil and more — sourced for purity, prepared with care. Buy below or visit the clinic.</Tx>
        </p>
      </div>

      {/* ── Universal Marketplace — direct links to every offering ── */}
      <section data-testid="marketplace-section" className="mb-14">
        <div className="mb-6">
          <h2 className="font-heading text-2xl sm:text-3xl text-forest font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-gold" /> <Tx>Everything in one place</Tx>
          </h2>
          <p className="text-sm text-mute mt-1"><Tx>Browse and buy any offering — lessons, books, exams and certificates — directly.</Tx></p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { to: "/academy/tutors", icon: GraduationCap, title: "Live Qur'an & Arabic Lessons", desc: "1-to-1 online sessions with tutors", price: "from £8", cta: "Book now" },
            { to: "/academy/pass", icon: Languages, title: "Academy Pass", desc: "30-day access to all learning tools", price: "£12", cta: "Subscribe" },
            { to: "/academy/certificates", icon: Award, title: "Qur'an Certificates & Ijāza", desc: "Juz' completion & full Ijāza", price: "£8 – £80", cta: "Get certified" },
            { to: "/hijama-atlas", icon: BookOpen, title: "Hijama Atlas — Digital Book", desc: "Full point-by-point PDF e-book", price: "Buy PDF", cta: "View book" },
            { to: "/services", icon: ScrollText, title: "Hijama Diploma Course", desc: "Complete training course materials", price: "Enrol", cta: "View course" },
            { to: "/services", icon: Video, title: "Hijama Video Exam & Certificate", desc: "Practical assessment + certificate", price: "Book exam", cta: "Start" },
            { to: "/search", icon: FlaskConical, title: "Illness Protocol PDFs", desc: "Prophetic remedy guides per condition", price: "Browse", cta: "Explore" },
          ].map((m) => {
            const Icon = m.icon;
            return (
              <Link key={m.title + m.to} to={m.to} data-testid={`marketplace-${m.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                className="clay-surface rounded-2xl p-5 flex flex-col lift-on-hover group">
                <div className="w-10 h-10 rounded-full bg-forest/10 text-forest flex items-center justify-center mb-3"><Icon className="w-5 h-5" /></div>
                <h3 className="font-heading text-forest font-semibold mb-1"><Tx>{m.title}</Tx></h3>
                <p className="text-xs text-mute leading-relaxed mb-4 flex-1"><Tx>{m.desc}</Tx></p>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gold"><Tx>{m.price}</Tx></span>
                  <span className="inline-flex items-center gap-1 text-xs text-forest font-medium group-hover:gap-2 transition-all">
                    <Tx>{m.cta}</Tx> <ArrowRight className="w-3.5 h-3.5 rtl:rotate-180" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Featured digital product — Hijama Atlas Book */}
      <div className="mb-14"><AtlasBookCard /></div>

      {/* Shop products grid (admin-curated) */}
      <section data-testid="shop-section">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-heading text-2xl sm:text-3xl text-forest font-bold flex items-center gap-2">
            <ShoppingBag className="w-6 h-6" /> <Tx>Shop</Tx>
          </h2>
          {products.length > 0 && (
            <p className="text-xs text-mute">{products.length} <Tx>{products.length === 1 ? "item" : "items"}</Tx></p>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-mute"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : products.length === 0 ? (
          <div className="clay-surface rounded-3xl p-10 text-center text-mute">
            <Sparkles className="w-6 h-6 mx-auto mb-3 text-gold" />
            <p className="text-sm mb-1"><Tx>Our shop is being stocked — products will appear here shortly.</Tx></p>
            <p className="text-xs"><Tx>In the meantime please contact the clinic for orders.</Tx></p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
            {products.map((p) => (
              <ShopProductCard key={p.id} product={p} onBuy={setBuying} />
            ))}
          </div>
        )}
      </section>

      {/* Contact-for-pricing for the in-clinic legacy products from clinic_info */}
      {clinic.products?.length > 0 && (
        <section className="mt-16">
          <h2 className="font-heading text-2xl text-forest font-bold mb-4"><Tx>Also stocked in-clinic</Tx></h2>
          <p className="text-sm text-mute mb-5 max-w-2xl">
            <Tx>Premium prophetic products available at the clinic — pricing varies. WhatsApp or call to order.</Tx>
          </p>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {clinic.products.map((p) => (
              <li key={p.name} className="clay-surface rounded-xl px-4 py-3 text-sm flex items-center justify-between">
                <div>
                  <p className="text-ink font-medium">{p.name}</p>
                  {p.arabic && <p className="arabic text-gold text-xs">{p.arabic}</p>}
                </div>
                <a href={`https://wa.me/${clinic.whatsapp}?text=${encodeURIComponent("Salam, I'd like to enquire about " + p.name)}`} target="_blank" rel="noreferrer" className="text-xs text-forest hover:text-gold inline-flex items-center gap-1">
                  <Mail className="w-3 h-3" /> Enquire
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      {buying && <ShopBuyModal product={buying} onClose={() => setBuying(null)} />}
    </div>
  );
};

export default Products;
